import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import EmailIcon from "@material-ui/icons/Email";
import { formatMagicLinkExpiry, isMagicLinkSession } from "Services/docForm.service";

const useStyles = makeStyles(() => ({
	root: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "280px",
		padding: "48px 24px",
		backgroundColor: "#fff",
		textAlign: "center",
	},
	icon: {
		fontSize: 56,
		color: "#9e9e9e",
		marginBottom: 16,
	},
	iconSuccess: {
		fontSize: 56,
		color: "#009A17",
		marginBottom: 16,
	},
	iconCaughtUp: {
		fontSize: 56,
		color: "#006ac6",
		marginBottom: 16,
	},
	title: {
		fontSize: 18,
		fontWeight: 500,
		color: "#333",
		marginBottom: 8,
	},
	description: {
		fontSize: 14,
		color: "#666",
		maxWidth: 460,
		lineHeight: 1.5,
		marginBottom: 20,
	},
	buttonRow: {
		display: "flex",
		gap: 12,
		flexWrap: "wrap",
		justifyContent: "center",
		marginBottom: 24,
	},
	primaryButton: {
		backgroundColor: "#006ac6",
		color: "#fff",
		textTransform: "none",
		padding: "8px 20px",
		"&:hover": {
			backgroundColor: "#0056a3",
		},
	},
	secondaryButton: {
		borderColor: "#006ac6",
		color: "#006ac6",
		textTransform: "none",
		padding: "8px 20px",
	},
	infoBox: {
		display: "flex",
		alignItems: "flex-start",
		gap: 8,
		backgroundColor: "#f0f7ff",
		border: "1px solid #c7dff7",
		borderRadius: 8,
		padding: "12px 16px",
		maxWidth: 420,
		textAlign: "left",
		marginTop: 4,
	},
	infoIcon: {
		fontSize: 18,
		color: "#006ac6",
		marginTop: 1,
		flexShrink: 0,
	},
	infoText: {
		fontSize: 13,
		color: "#004880",
		lineHeight: 1.5,
	},
}));

const COPY = {
	assigned: {
		Icon: CheckCircleOutlineIcon,
		iconClass: "iconCaughtUp",
		title: "You're all caught up",
		description:
			"No forms need your attention right now. New requests will appear here, and we'll also send you a magic link by email.",
		primaryLabel: "View completed",
		showMagicLinkHint: true,
	},
	completed: {
		Icon: CheckCircleOutlineIcon,
		iconClass: "icon",
		title: "No completed forms yet",
		description:
			"Forms you have finished will appear here. Open an assigned form and submit it to see it in this list.",
	},
	magicLink: {
		Icon: AssignmentOutlinedIcon,
		iconClass: "icon",
		title: "Sign in to see your forms",
		description:
			"You opened DocPlace with a magic link. Use the link in your email to fill that form, or sign in with your email and password to view your assigned and completed forms.",
	},
	magicLinkPending: {
		Icon: HourglassEmptyIcon,
		iconClass: "icon",
		title: "You have a form waiting",
		description:
			"Your secure link is active. Continue filling the form sent to your email before the link expires.",
		primaryLabel: "Continue filling",
	},
	magicLinkCompleted: {
		Icon: CheckCircleOutlineIcon,
		iconClass: "iconSuccess",
		title: "You already submitted this form",
		description:
			"Thanks — your responses were saved. You can review what you submitted while your link is still active.",
		primaryLabel: "View your submission",
	},
};

export default function FormsListEmptyState({
	variant = "assigned",
	onPrimaryAction,
	onSecondaryAction,
}) {
	const classes = useStyles();
	const config = COPY[variant] || COPY.assigned;
	const { Icon, iconClass, title, description, primaryLabel, showMagicLinkHint } = config;
	const expiryText = formatMagicLinkExpiry();
	const isMagicLink = isMagicLinkSession();
	const descriptionWithExpiry =
		variant === "magicLinkCompleted" || variant === "magicLinkPending"
			? `${description}${expiryText ? ` Link active until ${expiryText}.` : ""}`
			: description;

	return (
		<div className={classes.root} role="status">
			<Icon className={classes[iconClass]} aria-hidden />
			<Typography className={classes.title} component="h2">
				{title}
			</Typography>
			<Typography className={classes.description} component="p">
				{descriptionWithExpiry}
			</Typography>
			<div className={classes.buttonRow}>
				{primaryLabel && onPrimaryAction && (
					<Button
						variant="contained"
						className={classes.primaryButton}
						onClick={onPrimaryAction}
					>
						{primaryLabel}
					</Button>
				)}
				{isMagicLink && showMagicLinkHint && onSecondaryAction && (
					<Button
						variant="outlined"
						className={classes.secondaryButton}
						startIcon={<EmailIcon />}
						onClick={onSecondaryAction}
					>
						Resend email link
					</Button>
				)}
			</div>
			{showMagicLinkHint && (
				<div className={classes.infoBox}>
					<InfoOutlinedIcon className={classes.infoIcon} aria-hidden />
					<Typography className={classes.infoText}>
						<strong>How magic links work</strong>
						<br />
						Each email link is unique to you. After you submit, the link stays valid until
						expiry so you can come back to review.
					</Typography>
				</div>
			)}
		</div>
	);
}
